import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { ChevronDownIcon, PanelRightIcon, XIcon } from 'lucide-react';
import { useMemo } from 'react';

import { getInitials } from '../../../utils/getInitials';

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from '@/components/ui/collapsible';
import { Spinner } from '@/components/ui/spinner';

// GraphQL Query for Contact Right Panel data
const GET_CONTACT_RIGHT_PANEL = gql`
  query GetContactRightPanel($id: ID!) {
    node(id: $id) {
      ... on ContactNode {
        id
        name
        companyName
        createDate
        writeDate
        createUid {
          id
          name
        }
        writeUid {
          id
          name
        }
      }
    }
  }
`;

interface ContactRightPanelData {
  companyName: string;
  contactName: string;
  createdBy: string;
  createdOn: string;
  modifiedBy: string;
  modifiedOn: string;
}

interface ContactRightPanelNode {
  companyName?: null | string;
  createDate?: null | string;
  createUid?: { id: string; name: string } | null;
  id: string;
  name: string;
  writeDate?: null | string;
  writeUid?: { id: string; name: string } | null;
}

interface GetContactRightPanelData {
  node: ContactRightPanelNode | null;
}

interface GetContactRightPanelVariables {
  id: string;
}

export function ContactRightPanel({
  contactId,
  isOpen,
  onClose
}: {
  contactId: string;
  isOpen: boolean;
  onClose: () => void;
}) {
  const { data: apiData, loading } = useQuery<
    GetContactRightPanelData,
    GetContactRightPanelVariables
  >(GET_CONTACT_RIGHT_PANEL, {
    skip: !contactId || !isOpen,
    variables: { id: contactId }
  });

  const data = useMemo(
    () => mapContactRightPanelData(apiData?.node ?? null),
    [apiData]
  );

  if (!isOpen) return null;

  if (loading) {
    return (
      <div className="sticky top-0 z-10 -mt-16 flex h-[calc(100%+64px)] w-80 flex-col border-l border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900">
        <div className="grid h-full place-content-center">
          <Spinner className="size-8 text-blue-500" />
        </div>
      </div>
    );
  }

  return (
    <div className="sticky top-0 z-10 flex h-[calc(100dvh-64px)] w-80 flex-col border-l border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-200 p-4 dark:border-gray-700">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-full bg-cyan-100 text-sm font-medium text-cyan-700">
            {getInitials(data.contactName)}
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white">
              {data.contactName || '-'}
            </h3>
          </div>
        </div>
        <button
          className="p-1 text-gray-400 hover:text-gray-600"
          onClick={onClose}
          type="button"
        >
          <XIcon className="h-5 w-5" />
        </button>
      </div>

      {/* More Info Collapsible */}
      <Collapsible defaultOpen>
        <CollapsibleTrigger className="flex w-full items-center justify-between px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800">
          More Info
          <ChevronDownIcon className="h-4 w-4" />
        </CollapsibleTrigger>
        <CollapsibleContent className="px-4 pb-4">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-foreground text-sm">Modified On</span>
              <span className="text-sm text-gray-900 dark:text-white">
                {data.modifiedOn || '-'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-foreground text-sm">Created On</span>
              <span className="text-sm text-gray-900 dark:text-white">
                {data.createdOn || '-'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-foreground text-sm">Modified By</span>
              {data.modifiedBy ? (
                <div className="flex items-center gap-2">
                  <div className="flex h-5 w-5 items-center justify-center rounded-full bg-rose-100 text-xs font-medium text-rose-700">
                    {getInitials(data.modifiedBy)}
                  </div>
                  <span className="text-sm text-gray-900 dark:text-white">
                    {data.modifiedBy}
                  </span>
                </div>
              ) : (
                <span className="text-foreground text-sm">-</span>
              )}
            </div>
            <div className="flex items-center justify-between">
              <span className="text-foreground text-sm">Created By</span>
              {data.createdBy ? (
                <div className="flex items-center gap-2">
                  <div className="flex h-5 w-5 items-center justify-center rounded-full bg-blue-100 text-xs font-medium text-blue-700">
                    {getInitials(data.createdBy)}
                  </div>
                  <span className="text-sm text-gray-900 dark:text-white">
                    {data.createdBy}
                  </span>
                </div>
              ) : (
                <span className="text-foreground text-sm">-</span>
              )}
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

function formatDate(dateStr: null | string | undefined): string {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
}

function mapContactRightPanelData(
  node: ContactRightPanelNode | null
): ContactRightPanelData {
  if (!node) {
    return {
      companyName: '',
      contactName: '',
      createdBy: '',
      createdOn: '',
      modifiedBy: '',
      modifiedOn: ''
    };
  }
  return {
    companyName: node.companyName ?? '',
    contactName: node.name,
    createdBy: node.createUid?.name ?? '',
    createdOn: formatDate(node.createDate),
    modifiedBy: node.writeUid?.name ?? '',
    modifiedOn: formatDate(node.writeDate)
  };
}
export const ContactRightPanelOpenIcon = PanelRightIcon;
